/**
 * Quick Add (design Q2): turn dropped files into ready-to-post course content.
 * Videos (MP4) become recordings; documents become slides, readings, or syllabi.
 */
import { MAX_DOCUMENT_UPLOAD_BYTES, normalizeContentLink, LINK_ERROR_MESSAGE } from '../../../pages/admin/content/addContentModal/addContentHelpers';

export const TYPE_LABELS = { recording: 'Recording', slide: 'Slides', reading: 'Reading', syllabus: 'Syllabus' };

const DOC_EXT = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'html', 'htm'];
const SLIDE_EXT = ['ppt', 'pptx'];

const ext = (name) => (String(name || '').split('.').pop() || '').toLowerCase();

export function isVideo(file) {
  return file?.type === 'video/mp4' || ext(file?.name) === 'mp4';
}

export function isDocument(file) {
  return DOC_EXT.includes(ext(file?.name));
}

/** First guess at the content type, or null when we can't post this file. */
export function guessType(file) {
  if (isVideo(file)) return 'recording';
  if (!isDocument(file)) return null;
  if (/syllabus/i.test(file.name)) return 'syllabus';
  if (SLIDE_EXT.includes(ext(file.name)) || /slide|deck|presentation/i.test(file.name)) return 'slide';
  return 'reading';
}

/** The types this file can be posted as. */
export function typeOptions(file) {
  if (isVideo(file)) return ['recording'];
  if (isDocument(file)) return ['slide', 'reading', 'syllabus'];
  return [];
}

/** "week7-divine_attributes.pdf" → "Week7 divine attributes". */
export function titleFromFileName(name) {
  const base = String(name || '').replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!base) return 'Untitled';
  return (base[0].toUpperCase() + base.slice(1)).slice(0, 255);
}

export function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

let seq = 0;
/** File → staged row. */
export function stageFile(file, week) {
  seq += 1;
  const type = guessType(file);
  const tooBig = type === 'syllabus' || type === 'slide' || type === 'reading' ? file.size > MAX_DOCUMENT_UPLOAD_BYTES : false;
  if (tooBig) {
    // Not postable, so no type or week to pick.
    return {
      key: `qa-${Date.now()}-${seq}`, file, name: file.name, size: file.size, title: '', type: null, week: 0, url: '',
      status: 'unsupported', percent: 0, error: 'This file is over 2 GB, which is too large to post here. Use Manage content instead.',
    };
  }
  return {
    key: `qa-${Date.now()}-${seq}`,
    file,
    name: file.name,
    size: file.size,
    title: titleFromFileName(file.name),
    type,
    week: type === 'syllabus' ? 0 : week,
    url: '', // optional link for slides, readings and syllabi (shown next to the file)
    status: type ? 'ready' : 'unsupported', // ready | uploading | done | error | unsupported
    percent: 0,
    error: type ? '' : 'This file type can’t be posted here. Use Manage content instead.',
  };
}

/** Two videos for the same day need different file names in storage. */
export function recordingSuffixes(items) {
  const recs = items.filter((i) => i.type === 'recording');
  const map = new Map();
  if (recs.length > 1) recs.forEach((r, i) => map.set(r.key, `-${i + 1}`));
  return map;
}

/** A row with just a link and no file (a reading, slide or syllabus entry). */
export function stageLink(week) {
  seq += 1;
  return {
    key: `qa-${Date.now()}-${seq}`, file: null, linkOnly: true, name: 'new link', size: 0, title: '', type: 'reading', week,
    url: '', status: 'ready', percent: 0, error: '',
  };
}

/** What stops this row from posting, or '' when it is fine. Recordings only need a title. */
export function itemProblem(item) {
  if (!String(item.title || '').trim()) return 'Add a title.';
  if (item.type === 'recording') return '';
  const link = normalizeContentLink(item.url);
  if (link === null) return LINK_ERROR_MESSAGE;
  if (!link && !item.file) return 'Add a link, or remove this row.';
  return '';
}
